import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Button, Typography, IconButton, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Grid, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Checkbox,
  FormControlLabel, Chip, Avatar, Skeleton, InputAdornment, Tooltip,
  Stack, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Tabs, Tab
} from '@mui/material';
import {
  Edit, Delete, Search, PersonAdd, Clear, Person, Business,
  Groups, Group, Close, ContentCopy, AdminPanelSettings, Add, RemoveCircleOutline, Computer
} from '@mui/icons-material';
import {
  getAllUsers, updateUser, deleteUser, generateInvite, getUserById,
  getUserGroups, updateUserRole, removeUserFromDepartment, removeUserFromTeam,
  GROUP_TYPE
} from '../api/services/userService/User';
import { addUserToClient, removeUserFromClient } from '../api/services/userService/Client';
import NodeAssignmentDialog from './NodeAssignmentDialog';

const StatusChip = ({ enabled }) => {
  const isActive = enabled !== false;
  return (
    <Chip
      size="small"
      label={isActive ? 'Active' : 'Disabled'}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 500,
        bgcolor: isActive ? 'success.main' : 'error.main',
        color: 'white',
        '& .MuiChip-label': { px: 1 }
      }}
    />
  );
};

const RoleBadge = ({ role }) => {
  const isAdmin = role === 'admin' || role === 'lead';
  return (
    <Chip
      size="small"
      label={role}
      sx={{
        height: 18,
        fontSize: '0.65rem',
        textTransform: 'capitalize',
        bgcolor: isAdmin ? 'warning.light' : 'action.selected',
        color: isAdmin ? 'warning.dark' : 'text.secondary'
      }}
    />
  );
};

const GroupBadges = ({ user }) => {
  const clientCount = user.clients?.length || 0;
  const deptCount = user.departments?.length || 0;
  const teamCount = user.teams?.length || 0;

  if (!clientCount && !deptCount && !teamCount) {
    return <Typography variant="caption" color="text.disabled">No groups</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5}>
      {clientCount > 0 && (
        <Tooltip title={user.clients.map(c => `${c.name} (${c.role})`).join(', ')}>
          <Chip
            icon={<Business sx={{ fontSize: '14px !important' }} />}
            label={clientCount}
            size="small"
            variant="outlined"
            sx={{ height: 22, '& .MuiChip-label': { px: 0.5 } }}
          />
        </Tooltip>
      )}
      {deptCount > 0 && (
        <Tooltip title={user.departments.map(d => `${d.name} (${d.role})`).join(', ')}>
          <Chip
            icon={<Groups sx={{ fontSize: '14px !important' }} />}
            label={deptCount}
            size="small"
            variant="outlined"
            sx={{ height: 22, '& .MuiChip-label': { px: 0.5 } }}
          />
        </Tooltip>
      )}
      {teamCount > 0 && (
        <Tooltip title={user.teams.map(t => `${t.name} (${t.role})`).join(', ')}>
          <Chip
            icon={<Group sx={{ fontSize: '14px !important' }} />}
            label={teamCount}
            size="small"
            variant="outlined"
            sx={{ height: 22, '& .MuiChip-label': { px: 0.5 } }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

export default function UserManagement({ userPermissions, sharedData, onRefresh }) {
  const { keycloak } = useKeycloak();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNodeDialog, setOpenNodeDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGroups, setUserGroups] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [editTab, setEditTab] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    company: '', position: '', enabled: true, isSuperAdmin: false,
    onlyServiceRoleUser: false
  });
  const [roleChanges, setRoleChanges] = useState({});
  const [newGroupData, setNewGroupData] = useState({ type: GROUP_TYPE.TEAM, groupId: '', role: 'member' });
  const [inviteData, setInviteData] = useState({
    email: '', groupId: '', type: GROUP_TYPE.TEAM, role: 1,
    baseUrl: window.location.origin.includes('localhost')
      ? 'https://piosphere.de/user' : window.location.origin + '/user',
    only_service_role_user: false
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [inviteLink, setInviteLink] = useState('');
  const [showInviteLink, setShowInviteLink] = useState(false);

  const canManage = useCallback(() => {
    if (!userPermissions) return false;
    return userPermissions.isSuperAdmin ||
      Object.values(userPermissions.clientRoles || {}).includes('admin') ||
      Object.values(userPermissions.departmentRoles || {}).includes('admin') ||
      Object.values(userPermissions.teamRoles || {}).some(role => ['lead', 'admin'].includes(role));
  }, [userPermissions]);

  const filterByPermissions = useCallback((list, type, departmentsList = null) => {
    if (!userPermissions || userPermissions.isSuperAdmin) return list;
    switch (type) {
      case 'clients':
        return list.filter(c => userPermissions.clientIds?.includes(c.id));
      case 'departments':
        return list.filter(d =>
          userPermissions.departmentIds?.includes(d.id) ||
          userPermissions.clientIds?.includes(d.clientId)
        );
      case 'teams':
        if (departmentsList) {
          const accessibleDepartmentIds = departmentsList
            .filter(d => 
              userPermissions.departmentIds?.includes(d.id) ||
              userPermissions.clientIds?.includes(d.clientId)
            )
            .map(d => d.id);
          
          return list.filter(t =>
            userPermissions.teamIds?.includes(t.id) ||
            accessibleDepartmentIds.includes(t.departmentId)
          );
        }
        return list.filter(t =>
          userPermissions.teamIds?.includes(t.id) ||
          userPermissions.departmentIds?.includes(t.departmentId)
        );
      default:
        return list;
    }
  }, [userPermissions]);

  const filteredDepartments = useMemo(() => {
    if (!selectedClientId) return departments;
    return departments.filter(d => d.clientId === selectedClientId);
  }, [departments, selectedClientId]);

  const filteredTeams = useMemo(() => {
    if (selectedDepartmentId) return teams.filter(t => t.departmentId === selectedDepartmentId);
    if (selectedClientId) {
      const deptIds = departments.filter(d => d.clientId === selectedClientId).map(d => d.id);
      return teams.filter(t => deptIds.includes(t.departmentId));
    }
    return teams;
  }, [teams, departments, selectedClientId, selectedDepartmentId]);

  useEffect(() => {
    if (sharedData) {
      setClients(filterByPermissions(sharedData.clients || [], 'clients'));
      const filteredDepts = filterByPermissions(sharedData.departments || [], 'departments');
      setDepartments(filteredDepts);
      setTeams(filterByPermissions(sharedData.teams || [], 'teams', sharedData.departments || []));

      let filtered = sharedData.users || [];

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(u =>
          (u.fullName || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term)
        );
      }

      if (selectedTeamId) {
        filtered = filtered.filter(u => u.teams?.some(t => t.id === selectedTeamId));
      } else if (selectedDepartmentId) {
        filtered = filtered.filter(u => u.departments?.some(d => d.id === selectedDepartmentId));
      } else if (selectedClientId) {
        filtered = filtered.filter(u => u.clients?.some(c => c.id === selectedClientId));
      }

      setTotalCount(filtered.length);
      const start = page * rowsPerPage;
      setUsers(filtered.slice(start, start + rowsPerPage));
    }
  }, [sharedData, page, rowsPerPage, selectedClientId, selectedDepartmentId, selectedTeamId, searchTerm, filterByPermissions]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedClientId('');
    setSelectedDepartmentId('');
    setSelectedTeamId('');
    setPage(0);
  };

  const parseFullName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  };

  const openEditDialog = async (userId) => {
    setLoading(true);
    setEditTab(0);
    try {
      await keycloak.updateToken(30);
      const [userRes, groupsRes] = await Promise.all([
        getUserById(userId, keycloak.token),
        getUserGroups(userId, keycloak.token)
      ]);

      if (userRes.success) {
        const u = userRes.user;
        setCurrentUser(u);
        const { firstName, lastName } = parseFullName(u.fullName);
        setFormData({
          firstName: u.firstName || firstName,
          lastName: u.lastName || lastName,
          email: u.email || '',
          phone: u.phone || '',
          company: u.company || '',
          position: u.position || '',
          enabled: u.enabled !== false,
          isSuperAdmin: u.superAdmin || false,
          onlyServiceRoleUser: u.only_service_role_user || u.onlyServiceRoleUser || false
        });

        let clients = (u.clients || []).map(c => ({ id: c.id, name: c.name || 'Unknown', role: c.role || 'member' }));
        let departments = (u.departments || []).map(d => ({ id: d.id, name: d.name || 'Unknown', role: d.role || 'member' }));
        let teams = (u.teams || []).map(t => ({ id: t.id, name: t.name || 'Unknown', role: t.role || 'member' }));

        if (groupsRes.success) {
          const getGroupNameFromShared = (id, type) => {
            if (type === GROUP_TYPE.CLIENT) return sharedData.clients.find(c => c.id === id)?.name;
            if (type === GROUP_TYPE.DEPARTMENT) return sharedData.departments.find(d => d.id === id)?.name;
            return sharedData.teams.find(t => t.id === id)?.name;
          };

          if (groupsRes.clients && groupsRes.clients.length > 0) {
            clients = groupsRes.clients.map(c => {
              const existing = clients.find(uc => uc.id === c.id);
              return {
                id: c.id,
                name: existing?.name || getGroupNameFromShared(c.id, GROUP_TYPE.CLIENT) || 'Unknown',
                role: c.role || existing?.role || 'member'
              };
            });
          }
          if (groupsRes.departments && groupsRes.departments.length > 0) {
            departments = groupsRes.departments.map(d => {
              const existing = departments.find(ud => ud.id === d.id);
              return {
                id: d.id,
                name: existing?.name || getGroupNameFromShared(d.id, GROUP_TYPE.DEPARTMENT) || 'Unknown',
                role: d.role || existing?.role || 'member'
              };
            });
          }
          if (groupsRes.teams && groupsRes.teams.length > 0) {
            teams = groupsRes.teams.map(t => {
              const existing = teams.find(ut => ut.id === t.id);
              return {
                id: t.id,
                name: existing?.name || getGroupNameFromShared(t.id, GROUP_TYPE.TEAM) || 'Unknown',
                role: t.role || existing?.role || 'member'
              };
            });
          }
        }

        setUserGroups({
          clients,
          departments,
          teams
        });

        const initRoles = {};
        clients.forEach(c => initRoles[c.id] = c.role || 'member');
        departments.forEach(d => initRoles[d.id] = d.role || 'member');
        teams.forEach(t => initRoles[t.id] = t.role || 'member');
        setRoleChanges(initRoles);

        setNewGroupData({ type: GROUP_TYPE.TEAM, groupId: '', role: 'member' });
        setOpenDialog(true);
      } else {
        setNotification({ open: true, message: userRes.error || 'Failed to load user', severity: 'error' });
      }
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateUserBasicInfo = async () => {
    return await updateUser({
      id: currentUser?.id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      company: formData.company,
      position: formData.position,
      onlyServiceRoleUser: formData.onlyServiceRoleUser
    }, keycloak.token);
  };

  const updateUserRolesAndSuperAdmin = async () => {
    const roleEntries = Object.entries(roleChanges);
    const superAdminChanged = formData.isSuperAdmin !== (currentUser?.superAdmin || false);
    const hasRoleChanges = roleEntries.length > 0;

    if (!hasRoleChanges && !superAdminChanged) {
      return { success: true };
    }

    if (hasRoleChanges) {
      for (const [gid, roleStr] of roleEntries) {
        let gtype = GROUP_TYPE.TEAM;
        let roleNum = roleStr === 'admin' || roleStr === 'lead' ? 2 : 1;
        if (userGroups?.clients?.some(c => c.id === gid)) gtype = GROUP_TYPE.CLIENT;
        else if (userGroups?.departments?.some(d => d.id === gid)) gtype = GROUP_TYPE.DEPARTMENT;
        
        const roleRes = await updateUserRole(
          currentUser.id, 
          gid, 
          gtype, 
          roleNum, 
          keycloak.token, 
          formData.isSuperAdmin
        );
        
        if (!roleRes.success) {
          return roleRes;
        }
      }
      return { success: true };
    }

    if (superAdminChanged) {
      const userGroupsRes = await getUserGroups(currentUser.id, keycloak.token);
      if (userGroupsRes.success) {
        const allGroups = [
          ...(userGroupsRes.clients || []).map(c => ({ id: c.id, type: GROUP_TYPE.CLIENT, role: c.role || 'member' })),
          ...(userGroupsRes.departments || []).map(d => ({ id: d.id, type: GROUP_TYPE.DEPARTMENT, role: d.role || 'member' })),
          ...(userGroupsRes.teams || []).map(t => ({ id: t.id, type: GROUP_TYPE.TEAM, role: t.role || 'member' }))
        ];
        
        if (allGroups.length > 0) {
          const firstGroup = allGroups[0];
          const roleNum = firstGroup.role === 'admin' || firstGroup.role === 'lead' ? 2 : 1;
          return await updateUserRole(
            currentUser.id, 
            firstGroup.id, 
            firstGroup.type, 
            roleNum, 
            keycloak.token, 
            formData.isSuperAdmin
          );
        } else {
          return { 
            success: false, 
            error: 'Cannot update super admin: user has no groups' 
          };
        }
      }
      return { success: false, error: 'Failed to fetch user groups' };
    }

    return { success: true };
  };

  const handleUserSubmit = async () => {
    if (!formData.firstName || !formData.lastName) {
      setNotification({ open: true, message: 'First and last name required', severity: 'error' });
      return;
    }

    try {
      await keycloak.updateToken(30);

      const basicInfoRes = await updateUserBasicInfo();
      if (!basicInfoRes.success) {
        setNotification({ open: true, message: basicInfoRes.error || 'Failed to update user info', severity: 'error' });
        return;
      }

      const rolesRes = await updateUserRolesAndSuperAdmin();
      if (!rolesRes.success) {
        setNotification({ 
          open: true, 
          message: rolesRes.error || 'Failed to update roles or super admin', 
          severity: 'warning' 
        });
      }

      setNotification({ open: true, message: 'User updated', severity: 'success' });
      if (onRefresh) await onRefresh();
      setOpenDialog(false);
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleAddToGroup = async () => {
    if (!newGroupData.groupId) {
      setNotification({ open: true, message: 'Select a group', severity: 'error' });
      return;
    }

    try {
      await keycloak.updateToken(30);
      const roleNum = newGroupData.role === 'admin' || newGroupData.role === 'lead' ? 2 : 1;

      if (newGroupData.type === GROUP_TYPE.CLIENT) {
        const res = await addUserToClient(newGroupData.groupId, currentUser.id, roleNum === 2 ? 1 : 0, keycloak.token);
        if (!res.success) throw new Error(res.error);
      } else {
        const res = await generateInvite({
          email: currentUser.email,
          groupId: newGroupData.groupId,
          type: newGroupData.type,
          role: roleNum,
          baseUrl: window.location.origin.includes('localhost')
            ? 'https://piosphere.de/user' : window.location.origin + '/user'
        }, keycloak.token);
        if (!res.success) throw new Error(res.error);
      }

      setNotification({ open: true, message: 'Added to group', severity: 'success' });

      const userRes = await getUserById(currentUser.id, keycloak.token);
      if (userRes.success) {
        const u = userRes.user;
        setUserGroups({
          clients: u.clients || [],
          departments: u.departments || [],
          teams: u.teams || []
        });
        const initRoles = {};
        (u.clients || []).forEach(c => initRoles[c.id] = c.role || 'member');
        (u.departments || []).forEach(d => initRoles[d.id] = d.role || 'member');
        (u.teams || []).forEach(t => initRoles[t.id] = t.role || 'member');
        setRoleChanges(initRoles);
      }

      setNewGroupData({ type: GROUP_TYPE.TEAM, groupId: '', role: 'member' });
      if (onRefresh) await onRefresh();
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleRemoveFromGroup = async (groupId, type) => {
    try {
      await keycloak.updateToken(30);
      let res;
      if (type === 'client') {
        res = await removeUserFromClient(groupId, currentUser.id, keycloak.token);
      } else if (type === 'department') {
        res = await removeUserFromDepartment(groupId, currentUser.id, keycloak.token);
      } else {
        res = await removeUserFromTeam(groupId, currentUser.id, keycloak.token);
      }

      if (res.success) {
        const userRes = await getUserById(currentUser.id, keycloak.token);
        if (userRes.success) {
          const u = userRes.user;
          setUserGroups({
            clients: u.clients || [],
            departments: u.departments || [],
            teams: u.teams || []
          });
        }
        setNotification({ open: true, message: 'Removed', severity: 'success' });
        if (onRefresh) await onRefresh();
      }
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleInviteSubmit = async () => {
    if (!inviteData.email || !inviteData.groupId) {
      setNotification({ open: true, message: 'Email and group required', severity: 'error' });
      return;
    }

    try {
      await keycloak.updateToken(30);
      const res = await generateInvite(inviteData, keycloak.token);

      if (res.success) {
        if (res.inviteLink) {
          setInviteLink(res.inviteLink);
          setShowInviteLink(true);
        }
        setNotification({ open: true, message: 'Invitation sent', severity: 'success' });
        setOpenInviteDialog(false);
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleDeleteUser = async () => {
    try {
      await keycloak.updateToken(30);
      const res = await deleteUser(currentUser.id, keycloak.token);
      if (res.success) {
        setNotification({ open: true, message: 'User deleted', severity: 'success' });
        if (onRefresh) await onRefresh();
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
      setOpenDeleteDialog(false);
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const getGroupName = (id, type) => {
    if (type === GROUP_TYPE.CLIENT) return clients.find(c => c.id === id)?.name || 'Unknown';
    if (type === GROUP_TYPE.DEPARTMENT) return departments.find(d => d.id === id)?.name || 'Unknown';
    return teams.find(t => t.id === id)?.name || 'Unknown';
  };

  const getAvailableGroups = () => {
    const existingIds = new Set();
    userGroups?.clients?.forEach(c => existingIds.add(c.id));
    userGroups?.departments?.forEach(d => existingIds.add(d.id));
    userGroups?.teams?.forEach(t => existingIds.add(t.id));

    if (newGroupData.type === GROUP_TYPE.CLIENT) {
      return clients.filter(c => !existingIds.has(c.id));
    } else if (newGroupData.type === GROUP_TYPE.DEPARTMENT) {
      return departments.filter(d => !existingIds.has(d.id));
    }
    return teams.filter(t => !existingIds.has(t.id));
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setNotification({ open: true, message: 'Copied', severity: 'success' });
  };

  const hasFilters = searchTerm || selectedClientId || selectedDepartmentId || selectedTeamId;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>Users</Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount} user{totalCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {canManage() && (
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => {
              setInviteData({
                email: '', groupId: '', type: GROUP_TYPE.TEAM, role: 1,
                baseUrl: window.location.origin.includes('localhost')
                  ? 'https://piosphere.de/user' : window.location.origin + '/user',
                only_service_role_user: false
              });
              setOpenInviteDialog(true);
            }}
            size="small"
          >
            Invite
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
            sx={{ width: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={selectedClientId}
              label="Client"
              onChange={e => { setSelectedClientId(e.target.value); setSelectedDepartmentId(''); setSelectedTeamId(''); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={!selectedClientId}>
            <InputLabel>Department</InputLabel>
            <Select
              value={selectedDepartmentId}
              label="Department"
              onChange={e => { setSelectedDepartmentId(e.target.value); setSelectedTeamId(''); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {filteredDepartments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={!selectedDepartmentId}>
            <InputLabel>Team</InputLabel>
            <Select
              value={selectedTeamId}
              label="Team"
              onChange={e => { setSelectedTeamId(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {filteredTeams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          {hasFilters && (
            <Button size="small" onClick={clearFilters} startIcon={<Clear />}>Clear</Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Groups</TableCell>
              <TableCell>Status</TableCell>
              {canManage() && <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={180} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={60} /></TableCell>
                  {canManage() && <TableCell><Skeleton width={60} /></TableCell>}
                </TableRow>
              ))
            ) : users.length > 0 ? (
              users.map(user => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
                        {(user.fullName || 'U')[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.fullName}
                          {user.superAdmin && (
                            <Chip
                              icon={<AdminPanelSettings sx={{ fontSize: '12px !important' }} />}
                              label="Admin"
                              size="small"
                              color="secondary"
                              sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                  </TableCell>
                  <TableCell><GroupBadges user={user} /></TableCell>
                  <TableCell><StatusChip enabled={user.enabled} /></TableCell>
                  {canManage() && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentUser(user);
                          setOpenNodeDialog(true);
                        }}
                        title="Manage Nodes"
                      >
                        <Computer fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditDialog(user.id)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => { setCurrentUser(user); setOpenDeleteDialog(true); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canManage() ? 5 : 4} sx={{ py: 8, textAlign: 'center' }}>
                  <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>Edit User</Typography>
            <IconButton onClick={() => setOpenDialog(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={editTab} onChange={(_, v) => setEditTab(v)}>
            <Tab label="Profile" />
            <Tab label="Groups" />
          </Tabs>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {editTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="First Name" required
                  value={formData.firstName}
                  onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Last Name" required
                  value={formData.lastName}
                  onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Email" disabled value={formData.email} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Phone"
                  value={formData.phone}
                  onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.enabled ? 1 : 0}
                    label="Status"
                    onChange={e => setFormData(p => ({ ...p, enabled: e.target.value === 1 }))}
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Company"
                  value={formData.company}
                  onChange={e => setFormData(p => ({ ...p, company: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Position"
                  value={formData.position}
                  onChange={e => setFormData(p => ({ ...p, position: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isSuperAdmin}
                      onChange={e => setFormData(p => ({ ...p, isSuperAdmin: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Super Admin</Typography>}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.onlyServiceRoleUser}
                      onChange={e => setFormData(p => ({ ...p, onlyServiceRoleUser: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Only Service Role User</Typography>
                      <Typography variant="caption" color="text.secondary">
                        User can only access services mentioned in service roles
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Group Memberships</Typography>
                  {(!userGroups?.clients?.length && !userGroups?.departments?.length && !userGroups?.teams?.length) ? (
                    <Typography variant="body2" color="text.secondary">
                      Not assigned to any groups
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {userGroups?.clients?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Business fontSize="small" /> Clients ({userGroups.clients.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {userGroups.clients.map(c => (
                              <Chip
                                key={c.id}
                                icon={<Business sx={{ fontSize: '14px !important' }} />}
                                label={`${c.name || getGroupName(c.id, GROUP_TYPE.CLIENT)} (${c.role || 'member'})`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {userGroups?.departments?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Groups fontSize="small" /> Departments ({userGroups.departments.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {userGroups.departments.map(d => (
                              <Chip
                                key={d.id}
                                icon={<Groups sx={{ fontSize: '14px !important' }} />}
                                label={`${d.name || getGroupName(d.id, GROUP_TYPE.DEPARTMENT)} (${d.role || 'member'})`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {userGroups?.teams?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Group fontSize="small" /> Teams ({userGroups.teams.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {userGroups.teams.map(t => (
                              <Chip
                                key={t.id}
                                icon={<Group sx={{ fontSize: '14px !important' }} />}
                                label={`${t.name || getGroupName(t.id, GROUP_TYPE.TEAM)} (${t.role || 'member'})`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Switch to Groups tab to modify memberships and roles
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {editTab === 1 && (
            <Box>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Add to Group</Typography>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={newGroupData.type}
                      label="Type"
                      onChange={e => setNewGroupData(p => ({ ...p, type: e.target.value, groupId: '' }))}
                    >
                      <MenuItem value={GROUP_TYPE.CLIENT}>Client</MenuItem>
                      <MenuItem value={GROUP_TYPE.DEPARTMENT}>Department</MenuItem>
                      <MenuItem value={GROUP_TYPE.TEAM}>Team</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Group</InputLabel>
                    <Select
                      value={newGroupData.groupId}
                      label="Group"
                      onChange={e => setNewGroupData(p => ({ ...p, groupId: e.target.value }))}
                    >
                      {getAvailableGroups().map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 90 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={newGroupData.role}
                      label="Role"
                      onChange={e => setNewGroupData(p => ({ ...p, role: e.target.value }))}
                    >
                      <MenuItem value="member">Member</MenuItem>
                      <MenuItem value={newGroupData.type === GROUP_TYPE.TEAM ? 'lead' : 'admin'}>
                        {newGroupData.type === GROUP_TYPE.TEAM ? 'Lead' : 'Admin'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="contained" size="small" onClick={handleAddToGroup} disabled={!newGroupData.groupId}>
                    <Add fontSize="small" />
                  </Button>
                </Stack>
              </Box>

              {userGroups?.clients?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Clients</Typography>
                  {userGroups.clients.map(c => (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Business fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ flex: 1 }}>{c.name || getGroupName(c.id, GROUP_TYPE.CLIENT)}</Typography>
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={roleChanges[c.id] || 'member'}
                          onChange={e => setRoleChanges(p => ({ ...p, [c.id]: e.target.value }))}
                          variant="standard"
                        >
                          <MenuItem value="member">Member</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton size="small" color="error" onClick={() => handleRemoveFromGroup(c.id, 'client')}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {userGroups?.departments?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Departments</Typography>
                  {userGroups.departments.map(d => (
                    <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Groups fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ flex: 1 }}>{d.name || getGroupName(d.id, GROUP_TYPE.DEPARTMENT)}</Typography>
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={roleChanges[d.id] || 'member'}
                          onChange={e => setRoleChanges(p => ({ ...p, [d.id]: e.target.value }))}
                          variant="standard"
                        >
                          <MenuItem value="member">Member</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton size="small" color="error" onClick={() => handleRemoveFromGroup(d.id, 'department')}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {userGroups?.teams?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Teams</Typography>
                  {userGroups.teams.map(t => (
                    <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Group fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ flex: 1 }}>{t.name || getGroupName(t.id, GROUP_TYPE.TEAM)}</Typography>
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={roleChanges[t.id] || 'member'}
                          onChange={e => setRoleChanges(p => ({ ...p, [t.id]: e.target.value }))}
                          variant="standard"
                        >
                          <MenuItem value="member">Member</MenuItem>
                          <MenuItem value="lead">Lead</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton size="small" color="error" onClick={() => handleRemoveFromGroup(t.id, 'team')}>
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {!userGroups?.clients?.length && !userGroups?.departments?.length && !userGroups?.teams?.length && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Not assigned to any groups
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUserSubmit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openInviteDialog} onClose={() => setOpenInviteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>Invite User</Typography>
            <IconButton onClick={() => setOpenInviteDialog(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth size="small" label="Email" type="email" required
              value={inviteData.email}
              onChange={e => setInviteData(p => ({ ...p, email: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Group Type</InputLabel>
              <Select
                value={inviteData.type}
                label="Group Type"
                onChange={e => setInviteData(p => ({ ...p, type: e.target.value, groupId: '' }))}
              >
                <MenuItem value={GROUP_TYPE.TEAM}>Team</MenuItem>
                <MenuItem value={GROUP_TYPE.DEPARTMENT}>Department</MenuItem>
                <MenuItem value={GROUP_TYPE.CLIENT}>Client</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Group</InputLabel>
              <Select
                value={inviteData.groupId}
                label="Group"
                onChange={e => setInviteData(p => ({ ...p, groupId: e.target.value }))}
              >
                {inviteData.type === GROUP_TYPE.TEAM && teams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                {inviteData.type === GROUP_TYPE.DEPARTMENT && departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                {inviteData.type === GROUP_TYPE.CLIENT && clients.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteData.role}
                label="Role"
                onChange={e => setInviteData(p => ({ ...p, role: e.target.value }))}
              >
                <MenuItem value={1}>Member</MenuItem>
                <MenuItem value={2}>{inviteData.type === GROUP_TYPE.TEAM ? 'Lead' : 'Admin'}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inviteData.only_service_role_user}
                  onChange={e => setInviteData(p => ({ ...p, only_service_role_user: e.target.checked }))}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Only Service Role User</Typography>
                  <Typography variant="caption" color="text.secondary">
                    User can only access services mentioned in service roles
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenInviteDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInviteSubmit}>Send Invite</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs">
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{currentUser?.fullName}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteUser}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showInviteLink} onClose={() => setShowInviteLink(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invitation Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" multiline rows={2}
            value={inviteLink}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteLink(false)}>Close</Button>
          <Button variant="contained" startIcon={<ContentCopy />} onClick={copyInviteLink}>Copy</Button>
        </DialogActions>
      </Dialog>

      <NodeAssignmentDialog
        open={openNodeDialog}
        onClose={() => setOpenNodeDialog(false)}
        entityType="user"
        entityId={currentUser?.id}
        entityName={currentUser?.fullName || currentUser?.email}
        onSuccess={onRefresh}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(p => ({ ...p, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ minWidth: 200 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
